"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleQuestionMark as HelpCircle, MessageSquare, ShieldCheck, Sparkles } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "faq-1",
  question: "What is AI search optimisation?",
  answer: "AI search optimisation is the practice of improving how easily AI-powered search and answer tools, such as ChatGPT, Google AI Overviews, Perplexity and Bing Copilot, can find, understand and reference your business. It builds on traditional SEO foundations such as crawlability, helpful content, authority and technical health, then adds attention to answer-ready content, clear brand information and how your business is described across the web."
}, {
  id: "faq-2",
  question: "Is AI search optimisation the same as AI SEO, GEO, AEO and LLM optimisation?",
  answer: "They overlap. Generative engine optimisation (GEO) is the broadest term for improving how a brand is represented in generated answers. Answer engine optimisation (AEO) focuses on direct-answer surfaces such as featured snippets and voice results. LLM optimisation looks at how content is structured so language models can read and reference it. 'AI SEO' is used loosely, sometimes meaning SEO for AI search and sometimes meaning SEO done with AI tools. I treat them as one job rather than separate budgets."
}, {
  id: "faq-3",
  question: "Can you guarantee my business will be cited by ChatGPT or Google AI Overviews?",
  answer: "No, and nobody honestly can. AI platforms differ in how they crawl, retrieve, summarise and cite information, and the same prompt can give different sources in different sessions. What I can do is improve the clarity, accessibility and credibility of your content so that, when a platform retrieves your pages, they are well placed to be understood and referenced."
}, {
  id: "faq-4",
  question: "How do I optimise my site for ChatGPT and Perplexity?",
  answer: "Start with the basics: make sure your key pages are public, crawlable and not blocked by robots.txt or logins. Then answer the questions your buyers actually ask in clear, specific paragraphs, state your services, coverage and credentials plainly, and support claims with dates and named sources. Credible third-party mentions, reviews and consistent business details can also help. I test prompts across several platforms for your sector rather than assuming one universal rule."
}, {
  id: "faq-5",
  question: "Does structured data help AI search visibility?",
  answer: "Structured data helps search engines and AI systems interpret what a page is about, so I recommend it where it is accurate and relevant, such as organisation, service, FAQ and article markup. It does not guarantee that a page will be cited, and there is no special schema that guarantees inclusion in Google AI Overviews."
}, {
  id: "faq-6",
  question: "Do I still need traditional SEO?",
  answer: "Yes. Google AI Overviews and AI Mode draw on Google's index, Bing Copilot relies on Bing's, and AI tools tend to favour pages that are crawlable, relevant and credible. Strong foundations, including technical SEO, helpful content and authority, support both organic rankings and AI discovery. Traditional rankings no longer guarantee AI visibility, but they remain an important baseline."
}, {
  id: "faq-7",
  question: "How do you measure AI search performance?",
  answer: "I combine several imperfect signals: referral traffic from identifiable AI sources where data exists, qualified enquiries, calls and bookings, traditional organic metrics, and repeated sampling of AI answers using a consistent set of buyer prompts. Because AI outputs vary and many tools send no standard referral data, I report directional trends and examples rather than exact counts."
}, {
  id: "faq-8",
  question: "How long does AI search optimisation take to show results?",
  answer: "It depends on your starting point, your sector, how quickly technical and content changes are implemented and how the platforms behave. I do not promise results within a fixed number of weeks. Early progress is usually visible in completed improvements and in sampled answers before it appears in enquiries, and I report on all three."
}, {
  id: "faq-9",
  question: "Do AI search optimisation and my SEO audit overlap?",
  answer: "A general SEO audit reviews your overall organic performance, while an AI search visibility audit looks at how your brand appears in sampled AI answers, which sources are referenced, and what limits AI crawlers or models from understanding your site. Many businesses begin with one and use the findings to plan the other. See the options above for the audit and ongoing support I currently offer."
}, {
  id: "faq-10",
  question: "Can AI search optimisation work for a small business?",
  answer: "Yes, as long as expectations are realistic. For many small and medium-sized businesses the biggest gains come from restructuring and clarifying existing content, fixing technical barriers and making business information consistent, rather than publishing large volumes of new content. I will tell you honestly where the effort is likely to be worthwhile before you commit to anything."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqList.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
  return <section id="faqaistrategy" data-nav="light" className="relative w-full bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={fadeInVariants} className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5 rounded-full bg-secondary text-secondary-foreground">
            <Sparkles className="size-3.5 text-primary" />
            AI Search Strategy &amp; FAQs
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-foreground">
            Frequently Asked Questions About AI Search Optimisation
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Honest, forward-thinking perspectives from an independent SEO consultant. Understand how AI search affects your traffic, rankings, and sustainable business growth.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.3
        }} variants={fadeInVariants} className="lg:col-span-4 flex flex-col gap-6">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">AI Search Collaboration</span>
                </div>
              </div>

              <p className="text-sm text-muted-foreground font-default leading-relaxed mb-6">
                Questions about your own situation? Talk it through with me.
              </p>

              <div className="space-y-3 pt-2 border-t border-border mb-6">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground">
                  <div className="size-1.5 rounded-full bg-primary shrink-0" />
                  <span>Transparent AI &amp; organic visibility roadmaps</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground">
                  <div className="size-1.5 rounded-full bg-primary shrink-0" />
                  <span>No guaranteed rankings or citations</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground">
                  <div className="size-1.5 rounded-full bg-primary shrink-0" />
                  <span>Direct consultant communication via call &amp; email</span>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <Button asChild size="lg" className="w-full justify-between group">
                  <Link to="/ContactPage">
                    <span>Book a Free SEO Consultation</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full justify-center">
                  <Link to="/CaseStudiesPage">
                    Explore Case Studies
                  </Link>
                </Button>
              </div>
            </div>

            <div className="bg-muted/50 border border-border rounded-xl p-5 flex items-center gap-4">
              <div className="size-10 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground shrink-0">
                <MessageSquare className="size-5" />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-foreground font-default">Have a specific AI search query?</p>
                <p className="text-xs text-muted-foreground">
                  Email me directly at{" "}
                  <a href="mailto:contact@ye-consultancy.com" className="text-primary underline hover:text-primary/80 font-medium">
                    contact@ye-consultancy.com
                  </a>
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariants} className="lg:col-span-8 bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-md">
            <Accordion type="single" collapsible defaultValue="faq-1" className="w-full space-y-4">
              {faqList.map((item, i) => <AccordionItem data-index={i} key={item.id} value={item.id} className="border border-border rounded-xl px-4 sm:px-6 transition-colors hover:border-primary/40 data-[state=open]:border-primary data-[state=open]:bg-background/60">
                  <AccordionTrigger className="py-5 hover:no-underline text-left text-base sm:text-lg font-semibold font-default text-foreground gap-4">
                    <span className="flex items-center gap-3">
                      <HelpCircle className="size-4 text-primary shrink-0 hidden sm:inline-block" />
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 pt-1 text-sm sm:text-base text-muted-foreground font-default leading-relaxed border-t border-border/50 mt-1">
                    <p>{item.answer}</p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>;
}